import {
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
    useLocation,
    useMatch,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';

import {
    getCatalogItemsShowDetailedView,
    getProposalsLoading,
} from 'entities/CatalogItem/model/selectors/catalogItemSelectors';
import { fetchCatalogItemById } from 'entities/CatalogItem/model/services/fetchCatalogItemById/fetchCatalogItemById';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { DisplayItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    getGlobalCategoriesList,
    getLocalCategoriesList,
    getSelectSubCategoryName,
} from 'entities/Category/model/selectors/categorySelectors';
import { getSelectedPropertyCatalogId } from 'entities/Property/model/selectors/PropertySelectors';
import {
    getUserCanEditPropertyCatalog,
    getUserCanSeePrices,
} from 'entities/User/model/selectors/userPermissionsSeletors';
import {
    VendorActivationStatus,
    getPropertyVendorsMap,
} from 'entities/Vendors';
import {
    getEditMinAllowedInStockIsLoading,
    getEditMinAllowedInStockValue,
    getEditMinAllowedInStockValueIsChanged,
} from 'features/EditMinAllowedInStock';
import { editMinAllowedService } from 'features/EditMinAllowedInStock/model/services/editMinAllowedService';
import { getPropertyCatalogId } from 'features/fetchPropertyById/model/selectors/fetchPropertyByIdSelectors';
import {
    getRouteGlobalCatalog,
    getRouteOrderMarketplaceCategory,
    getRouteOrdersAddItemsMarketplace,
    getRoutePropertiesCatalogInventory,
    getRoutePropertiesCatalogSelected,
    getRoutePropertiesGlobalCatalogSelected,
} from 'shared/const/router';
import { useIsOrdersAddItemsMarketplace } from 'shared/lib/contexts/CatalogIdentityContext';
import { useActiveTabOnScroll } from 'shared/lib/hooks/useActiveTabOnScroll';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useFeatureFlag } from 'shared/lib/hooks/useFeatureFlag';
import { useImageLightbox } from 'shared/ui/ImageLightbox';
import { PropertyCatalogItemsMoveTo } from 'widgets/PropertyCatalog/models/consts/propertyCatalogItemsListTypes';

interface UsePropertyCatalogItemDetailedHooksProps {
    id: string;
    displayItem: DisplayItem;
    isOrderPage: boolean;
    minAllowedInStock: number;
    moveTo?: PropertyCatalogItemsMoveTo;
    isLocal: boolean;
    onCancel: () => void;
    scrollContainerRef: React.RefObject<HTMLDivElement>;
}
export const usePropertyCatalogItemDetailedHooks = ({
    id,
    displayItem,
    isOrderPage,
    minAllowedInStock,
    moveTo,
    isLocal,
    onCancel,
    scrollContainerRef,
}: UsePropertyCatalogItemDetailedHooksProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();

    const isOpened = useSelector(getCatalogItemsShowDetailedView);
    const vendorsMap = useSelector(getPropertyVendorsMap);

    // In order context, catalogId might come from location state
    const propertyCatalogId = useSelector(getPropertyCatalogId);
    const selectedPropertyCatalogId = useSelector(getSelectedPropertyCatalogId);
    const catalogIdFromState = location.state?.selectedPropertyCatalogId;
    const catalogId =
        catalogIdFromState || propertyCatalogId || selectedPropertyCatalogId;

    const minAllowedValue = useSelector(getEditMinAllowedInStockValue);
    const loading = useSelector(getEditMinAllowedInStockIsLoading);
    const proposalsLoading = useSelector(getProposalsLoading);
    const canEditCatalog = useSelector(getUserCanEditPropertyCatalog);
    const catalogPricesVisible = useFeatureFlag('catalog_prices_is_visible');
    const canSeePrices =
        useSelector(getUserCanSeePrices) && catalogPricesVisible;

    const isMoveToCatalog = moveTo === PropertyCatalogItemsMoveTo.CATALOG;
    const showActionsPanel = isMoveToCatalog && isLocal;

    const isGlobalCatalog = useMatch(
        getRouteGlobalCatalog(':propertyId') + '/*',
    );

    const isInventoryTable = useMatch(
        getRoutePropertiesCatalogInventory(':propertyId') + '/*',
    );

    const isPropertyCatalogRoute = useMatch(
        getRoutePropertiesCatalogSelected(':propertyId', ':categoryId') + '/*',
    );

    const valueChanged = useSelector(getEditMinAllowedInStockValueIsChanged);

    const [initialMinAllowedInStock, setInitialMinAllowedInStock] =
        useState(minAllowedInStock);

    const proposalsFetchAttemptedForIdRef = useRef<string | null>(null);

    const { openLightbox } = useImageLightbox();

    // Determine available tabs based on content
    const availableTabs = useMemo(() => {
        const tabs = ['details', 'suppliers'];
        if (
            displayItem.specifications &&
            Object.keys(displayItem.specifications).length > 0
        ) {
            tabs.push('specifications');
        }
        return tabs;
    }, [displayItem.specifications]);

    // Scroll-aware tab management
    const { activeTab, setActiveTab } = useActiveTabOnScroll({
        scrollContainerRef,
        tabValues: availableTabs,
        getSectionId: (tabValue) => `section-${tabValue}`,
        defaultTab: 'details',
    });

    const globalCategories = useSelector(getGlobalCategoriesList);
    const localCategories = useSelector(getLocalCategoriesList);
    const subCategoryName = useSelector(getSelectSubCategoryName);

    const isOrderMarketplace = useIsOrdersAddItemsMarketplace();
    const currentPageIsOrders = useMatch('/orders' + '/*');

    const showEditOnHand =
        isLocal && !isGlobalCatalog && !currentPageIsOrders && canEditCatalog;

    const getCategoryName = useCallback(
        (categoryId: string | number) => {
            if (localCategories?.[categoryId])
                return localCategories?.[categoryId].title;
            if (globalCategories?.[categoryId])
                return globalCategories?.[categoryId].title;
            return 'Category';
        },
        [localCategories, globalCategories],
    );

    const hydratedSpecifications = useMemo(() => {
        const specifications = structuredClone(
            displayItem.specifications || {},
        );

        if (displayItem.prop65Warning && displayItem.prop65Warning.length > 0) {
            specifications['Proposition 65'] = displayItem.prop65Warning;
        }

        return specifications;
    }, [displayItem.specifications, displayItem.prop65Warning]);

    const backButtonText = useMemo(() => {
        if (isOrderMarketplace) {
            return subCategoryName || 'Marketplace';
        }
        if (isOrderPage) {
            return 'Order';
        }
        if (isInventoryTable) {
            return 'Inventory';
        }
        if (params?.categoryId) {
            return getCategoryName(params?.categoryId);
        }
        return 'Catalog';
    }, [
        isOrderMarketplace,
        subCategoryName,
        isOrderPage,
        isInventoryTable,
        params?.categoryId,
        getCategoryName,
    ]);

    const goBack = useCallback(() => {
        if (isOrderMarketplace) {
            const categoryRoute = params?.categoryId
                ? getRouteOrderMarketplaceCategory(params.categoryId)
                : getRouteOrdersAddItemsMarketplace();
            navigate(categoryRoute, { state: location.state });
            onCancel();
            return;
        }

        if (isOrderPage) {
            navigate(-1);
            onCancel();
            return;
        }

        if (isGlobalCatalog) {
            navigate(
                getRoutePropertiesGlobalCatalogSelected(
                    params?.propertyId,
                    params?.categoryId,
                ),
            );
        } else if (isInventoryTable) {
            navigate(-1);
        } else if (isPropertyCatalogRoute) {
            navigate(
                getRoutePropertiesCatalogSelected(
                    params?.propertyId,
                    params?.categoryId,
                ),
            );
        }
        onCancel();
    }, [
        isOrderMarketplace,
        isOrderPage,
        isGlobalCatalog,
        isInventoryTable,
        isPropertyCatalogRoute,
        onCancel,
        params.categoryId,
        params?.propertyId,
        navigate,
        location.state,
    ]);

    const onSubmit = useCallback(async () => {
        const data = {
            items: [{ id, minAllowedInStock: minAllowedValue }],
        };

        const response = await dispatch(
            editMinAllowedService({ catalogId, data }),
        );

        if (response.meta.requestStatus == 'fulfilled') {
            setInitialMinAllowedInStock(minAllowedValue);

            if (isMoveToCatalog) {
                dispatch(
                    catalogItemActions.updateItem(response.payload.patched[0]),
                );
            }

            toast(t('Min on Hand value updated'));
        }
    }, [
        id,
        minAllowedValue,
        catalogId,
        dispatch,
        isMoveToCatalog,
        t,
        setInitialMinAllowedInStock,
    ]);

    const refreshDetailedItem = useCallback(async () => {
        if (!catalogId || !id || !isLocal) {
            return;
        }

        await dispatch(
            fetchCatalogItemById({
                catalogId,
                itemId: id,
            }),
        );
    }, [catalogId, dispatch, id, isLocal]);

    const handleTabChange = useCallback(
        (event: SyntheticEvent<Element, Event>, newValue: string) => {
            setActiveTab(newValue);
        },
        [setActiveTab],
    );

    const proposalIsActive = useCallback(
        (sellerUid: string) => {
            const vendor = vendorsMap?.[sellerUid];
            if (!vendor) {
                return false;
            }
            // NOTE: Renovations are not currently supported.
            // When renovations feature is added, also check renovationsAccountStatus
            // when renovationEnabled is true.
            return (
                vendor.operationsAccountStatus ===
                VendorActivationStatus.VERIFIED
            );
        },
        [vendorsMap],
    );

    const handleOpenLightbox = useCallback(() => {
        openLightbox(displayItem.imageUrl);
    }, [openLightbox, displayItem.imageUrl]);

    useEffect(() => {
        proposalsFetchAttemptedForIdRef.current = null;
    }, [id]);

    // Fetch item with proposals when detail view is opened and proposals are missing
    useEffect(() => {
        const shouldFetch =
            isOpened &&
            catalogId &&
            id &&
            typeof displayItem.proposals === 'undefined' &&
            !proposalsLoading &&
            proposalsFetchAttemptedForIdRef.current !== id;

        if (shouldFetch) {
            proposalsFetchAttemptedForIdRef.current = id;
            dispatch(
                fetchCatalogItemById({
                    catalogId,
                    itemId: id,
                }),
            );
        }
    }, [
        isOpened,
        catalogId,
        id,
        displayItem.proposals,
        proposalsLoading,
        dispatch,
    ]);

    useEffect(
        () => () => {
            if (isOpened) {
                dispatch(catalogItemActions.closeDetailedItem());
            }
        },
        [dispatch, isOpened],
    );

    return {
        t,
        hydratedSpecifications,
        backButtonText,
        showActionsPanel,
        canEditCatalog,
        canSeePrices,
        isGlobalCatalog,
        isInventoryTable,
        initialMinAllowedInStock,
        showEditOnHand,
        valueChanged,
        loading,
        proposalsLoading,
        proposalIsActive,
        goBack,
        onSubmit,
        refreshDetailedItem,
        activeTab,
        handleTabChange,
        handleOpenLightbox,
    };
};
